import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Search, Lightbulb, ArrowLeft, BookOpen } from "lucide-react";
import { AnalysisResults } from "@/components/AnalysisResults";
import { toast } from "sonner";
import { APP_CONFIG, UI_TEXT } from "@/constants";
import { FALLACY_EXAMPLES, EXAMPLE_CATEGORIES } from "@/constants/examples";
import { Link } from "react-router-dom";
import { useFallacyAnalysis } from "@/hooks/ai/useFallacyAnalysis";
import { validateAnalysisText, isValidTextLength } from "@/utils/validation/textValidation";
import { formatCountText } from "@/utils/data/fallacyHelpers";

const Analyze = () => {
  const [inputText, setInputText] = useState("");
  const [showExamples, setShowExamples] = useState(false);
  
  const { analyzeText } = useFallacyAnalysis();

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    if (!isValidTextLength(value)) {
      toast.error(`Text is too long. Please keep it under ${APP_CONFIG.maxTextLength} characters.`);
      return;
    }
    
    setInputText(value);
  };

  const loadExample = (categoryKey: string, exampleIndex: number) => {
    const category = FALLACY_EXAMPLES[categoryKey as keyof typeof FALLACY_EXAMPLES];
    if (category && category[exampleIndex]) {
      setInputText(category[exampleIndex].text);
      setShowExamples(false);
      toast.success(`${UI_TEXT.messages.loadedExample}: ${category[exampleIndex].title}`);
    }
  };

  const handleAnalyze = async () => {
    const validation = validateAnalysisText(inputText);
    
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    analyzeText.mutate(validation.sanitized!);
  };

  const clearAnalysis = () => {
    analyzeText.reset();
    setInputText("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {UI_TEXT.buttons.backToGuide}
            </Button>
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-800 to-indigo-800 bg-clip-text text-transparent">
                Analyze Text for Fallacies
              </h1>
            </div>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Use AI to identify logical fallacies and improve your arguments
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-blue-600" />
                {UI_TEXT.headers.fallacyAnalysis}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Example Selection Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-slate-700">
                    {UI_TEXT.buttons.tryExamples}
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExamples(!showExamples)}
                    className="text-xs"
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    {showExamples ? UI_TEXT.buttons.hideExamples : UI_TEXT.buttons.showExamples}
                  </Button>
                </div>
                
                {showExamples && (
                  <div className="space-y-3 p-3 bg-slate-50/50 rounded-lg border">
                    <p className="text-xs text-slate-600">
                      {UI_TEXT.messages.selectExample}
                    </p>
                    {EXAMPLE_CATEGORIES.map((category) => (
                      <div key={category.key} className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-1">
                          <span>{category.icon}</span>
                          {category.label}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {FALLACY_EXAMPLES[category.key].map((example, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              onClick={() => loadExample(category.key, index)}
                              className="text-xs h-auto p-2 text-left justify-start hover:bg-blue-50"
                              disabled={analyzeText.isPending}
                            >
                              <div className="truncate">
                                <div className="font-medium">{example.title}</div>
                                <div className="text-slate-500 text-xs mt-0.5 line-clamp-1">
                                  {example.text.substring(0, 60)}...
                                </div>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="analysis-text">
                  {UI_TEXT.placeholders.analyzeText.split('...')[0]}
                </Label>
                <Textarea
                  id="analysis-text"
                  placeholder={UI_TEXT.placeholders.analyzeText}
                  value={inputText}
                  onChange={handleInputChange}
                  className="min-h-[120px] resize-none"
                  disabled={analyzeText.isPending}
                  maxLength={APP_CONFIG.maxTextLength}
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>{formatCountText(UI_TEXT.format.charactersRemaining, { 
                    current: inputText.length,
                    max: APP_CONFIG.maxTextLength
                  })}</span>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleAnalyze}
                  disabled={!inputText.trim() || analyzeText.isPending}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  {analyzeText.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {UI_TEXT.buttons.analyzing}
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4 mr-2" />
                      {UI_TEXT.buttons.analyzeText}
                    </>
                  )}
                </Button>
                
                {analyzeText.data && (
                  <Button 
                    onClick={clearAnalysis}
                    variant="outline"
                    className="hover:bg-slate-50"
                  >
                    {UI_TEXT.buttons.clearResults}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {analyzeText.data && (
            <AnalysisResults analysis={analyzeText.data} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Analyze;